import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Modal,
  Button,
  Tooltip,
  Popover,
  Table,
  InputNumber,
  Space,
  message
} from "antd";
import {
  QuestionCircleOutlined,
  CalculatorOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useTranslate } from "/src/lib/MrTranslate/MrTranslate";
import { useDispatch, useSelector } from "react-redux";
import { actions as experienceUserActions } from "/src/views/ExperienceUsers/redux";
import { activeAdjustedExperienceSelector } from "/src/views/Experiences/selector";
import { showConfirmModal } from "/src/components/UI/Segment/UIHelper";
import { experienceUsersCountSelector } from "../selector";
import { debounce } from "lodash";

// Combined error types and messages as single source of truth
const errorConfig = {
  small: "ConvertToAchievementLevel.must_be_more_than_previous_value",
  large: "ConvertToAchievementLevel.must_be_less_than_next_value", 
  empty: "ConvertToAchievementLevel.must_not_be_empty",
  max: "ConvertToAchievementLevel.must_be_less_than_the_maximum",
  min: "ConvertToAchievementLevel.must_be_more_than_the_minimum",
  whole: "ConvertToAchievementLevel.enter_a_whole_number"
};

const shouldDisableConvertButton = (dataSource) => {
  console.log("DataSource validation ===>", dataSource);
  const hasError = dataSource.some(item => item.error && errorConfig[item.error]);
  // Skip index 0 since its percentage is always '' and does not require validation
  const shouldDisableConvertButton = dataSource.some((item, idx) => item.percentage === '' && idx !== 0);
  return hasError || shouldDisableConvertButton;
}

//  setting input and error for every input on each input value change
//  error with condition that each input should be more than the previous value...
//  ...and less than the next value. making input blank if value is more than 100 or less than 0
const handleDataChange = (prevData, value, index) => {
  return prevData.map((obj, i) => {
    let { error, percentage } = obj;

    if (i === index) {
      percentage = value;
    }

    if ((i === index || error !== null) && i !== 8) {
      const prevPercentage = prevData[i - 1]?.percentage;
      const nextPercentage = prevData[i + 1]?.percentage;

      // Reset error first
      error = false;

      // Check errors in priority order - only the first applicable error will be set
      if (percentage === '' || percentage === null) {
        error = "empty";
        // set error to false if index is 0. as input is disabled for 0th index
        if(i === 0) {
          error = false;
        }
        percentage = '';
      } else if (percentage >= 100) {
        percentage = '';
        error = "max";
      } else if (percentage <= 0) {
        percentage = '';
        error = "min";
      } else if(!Number.isInteger(percentage)) {
        percentage = '';
        error = "whole";
      } else if (i - 1 === index && percentage <= value) {
        error = value ? "small" : false;
      } else if (i + 1 === index && percentage >= value) {
        error = value ? "large" : false;
      } else if (i - 1 !== index && percentage <= prevPercentage) {
        error = prevPercentage ? "small" : false;
      } else if (i + 1 !== index && percentage >= nextPercentage) {
        error = nextPercentage ? "large" : false;
      }
    }

    return { percentage, error };
  });
};


const ConvertToAchievementLevel = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [data, setData] = useState([]);  
  const [isLoading, setIsLoading] = useState(false)
  const activeExperience = useSelector(activeAdjustedExperienceSelector());
  const experienceUsersCount = useSelector(experienceUsersCountSelector())
  const isExperienceUsersExist = experienceUsersCount === 0 ? false : true
  const minPercentage = 0;
  const maxPercentage = 100;

  const mrIntl = useTranslate();
  const dispatch = useDispatch();


  useEffect(() => {
    let updatedDataSource;
    const criteriumAssociation = activeExperience?.criterium_associations?.[0]?.custom_fields?.cwp_al_conversions;

    let dataSource = criteriumAssociation?.type === "percentage" 
      ? criteriumAssociation?.ranges 
      : [];
    if (!dataSource || dataSource.length === 0) {
      // Creating a default array containing data for each input of the table.
      updatedDataSource = Array.from({ length: 10 }, (_, i) => {
        if (i === 0) return {percentage: minPercentage, error: false};
        if (i === 9) return {percentage: maxPercentage, error: false};
        return {percentage: '', error: null};
      });
    } else {
      updatedDataSource = dataSource.map((val, index) => ({percentage: val, error: false}))
    }

    if (dataSource.length === 11) {
      // Remove the first element (index 0) since the table displays data starting from index 1.
      // For backward compatibility: remove the second element (index 1) also.
      // In the new UI, achievement level 0 is not configurable by users.
      // Any percentage above 0 is mapped to Achievement Level 1.
      updatedDataSource.splice(0, 2);
    } else {
      // Remove the first element (index 0) since the table displays data starting from index 1.
      updatedDataSource.shift();
    }

    setData(updatedDataSource);
  }, []);

  const onInputChange = (value, index) => {
    setData((prevData) => {
      const newData = handleDataChange(prevData, value, index);
      return newData;
    });
  };

  const debouncedHandleInputChange = useCallback(
    debounce(onInputChange, 200),
    [data] 
  );


  const columns = [
    {
      title: mrIntl("ConvertToAchievementLevel.overall_criteria_points"),
      dataIndex: "overall_criteria_points",
      key: "overall_criteria_points",
      className: "overall-criteria-points-column",
      width: "50%",
    },
    {
      title: "",
      dataIndex: "to",
      key: "to",
      render: (text, record, index) => {
        return  <ArrowRightOutlined /> 
      },
      width: "20%"
    }
,
    {
      title: mrIntl("ConvertToAchievementLevel.equivalent_level"),
      dataIndex: "equivalent_level",
      key: "equivalent_level",
      className: "equivalent-level-column",
      width: "30%"
    },
  ];
  // using useMemo so that dataSource only change only by change of data 
  const dataSource = useMemo(() => {
    return data.map((obj, index) => {
      const value = obj.percentage
      // const error = validateInput(index, value, data); // handling error for every input here as error is not a state
      const error = obj.error;
      const errorMessage = error ? mrIntl(errorConfig[error]) : ""; // Check if error exists and has a corresponding error config
      const isFirstOrLastIndex = index !== 0 && index !== 8;
      
      return {
        key: `percentage-calculator-row-${index}`,
        overall_criteria_points: (
          <div className="overall-criteria-points-row">
            <Space className="overall-criteria-points-row-space">
            <div className={`range-text ${index === 0 ? "" : "bg-color-gray"}`}>
            <span className="percentage-text-span">
              {index === 0 ? null : 
               index === 1 ?  "> 0": 
               data[index - 1]?.percentage ?  `> ${data[index - 1].percentage}` : ''}
            </span>
            <span className={`percentage-text-span ${index === 0 ? "visibility-hidden" : ""}`}>%</span>
            </div>
            <span className={`range-separator ${index === 0 ? "visibility-hidden" : ""}`} >
              to
            </span>
            </Space> 
            <div className="percentage-input-container">
              <InputNumber
                className={`percentage-input ${error && isFirstOrLastIndex ? "error-bg" : ""}`}
                onChange={(value) => debouncedHandleInputChange(value, index)}
                value={index === 0 ? 0 : value}
                controls={false}
                disabled={index === 0 || index === 8}
                suffix="%"
              />
              {isFirstOrLastIndex && error && errorMessage && <div className="error-message">{errorMessage}</div>}
            </div>
          </div>
        ),
        equivalent_level: index,
      };
    });
  }, [data]);

  const calculateAchievementLevel = () => {
    // Extract all percentage values except the first one (index 0), since this value is not used in the al calculation
    const values = data.map(obj => obj.percentage).filter((value, index) => index !== 0);

    const dataToPost = {
      experience_id: activeExperience.id,
      type: "percentage",
      criteria_boundary: [0,0,...values]  //adding 0 back because i need to send in request which i removed in useEffect as it was not needed in table dataSource
    }
    console.log("dataToPost ==>", JSON.stringify(dataToPost));
    showConfirmModal({
      centered: true,
      title: mrIntl("ConvertToAchievementLevel.this_action_will_overwrite_any_existing_overall_achievement_levels_with"),
      cancelText: "Cancel",
      onOk: () => {
        setIsLoading(true)
        dispatch(
          experienceUserActions.convertToAchievementLevel(dataToPost, {
            successCallback: () => {
              message.success(mrIntl("ConvertToAchievementLevel.achievement_levels_calculated_successfully"));
              setIsLoading(false)
              setIsModalOpen(false);
            },
            errorCallback: () => {
              setIsLoading(false)
              message.error('Something went wrong')
            }
          })
        );
      },
      onCancel: () => {},
      mrIntl: mrIntl,
    });
  };
  
  const tooltipTitle = isExperienceUsersExist
    ? mrIntl("ConvertToAchievementLevel.convert_pointbased_scores_into_achievement_levels_on_a_scale_from")
    : "";

  return (
    <>
      <Tooltip
        placement="left"
        title={tooltipTitle}
      >
        <Button
          className="convert-to-achievement-level-button"
          type="text"
          ghost={true}
          onClick={() => setIsModalOpen(true)}
          icon={<CalculatorOutlined />}
          disabled={!isExperienceUsersExist}
        >
          {mrIntl("ConvertToAchievementLevel.convert_to_achievement_level")}
        </Button>
      </Tooltip>
      <Modal
        title={
          <span>
            {mrIntl(
              "ConvertToAchievementLevel.convert_to_achievement_level"
            )}
            <Popover
              content={
                <p className="convert-to-achievement-level-popover">
                  {mrIntl("ConvertToAchievementLevel.this_feature_allows_you_to_convert_overall_criteria_points_expressed")}
                </p>
              }
              trigger="hover"
            >
              <QuestionCircleOutlined style={{ marginLeft: 5 }} />
            </Popover>
          </span>
        }
        open={isModalOpen}
        footer={
          <Button 
            onClick={() => calculateAchievementLevel()} 
            disabled={shouldDisableConvertButton(data)} 
            loading = {isLoading}
            type="primary"
          >
            {mrIntl("ConvertToAchievementLevel.convert")}
          </Button>
        }
        onCancel={() => setIsModalOpen(false)}
        okText={mrIntl("CommonText.save")}
        wrapClassName="default-percentage-modal-wrapper"
        centered={true}
        getContainer={() => document.body}
      >
        <Table className="percentage-table" dataSource={dataSource} columns={columns} pagination={false} rowHoverable={false} scroll={{y: "70vh"}} />
      </Modal>
    </>
  );
};

export default ConvertToAchievementLevel;